import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Grid, Typography, Button, Chip, Avatar, LinearProgress, List, ListItem, ListItemAvatar, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, TextField, CircularProgress } from '@mui/material';
import { Add as AddIcon, School as TrainingIcon, Person as PersonIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const TrainingList = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [categories, setCategories] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignDialog, setAssignDialog] = useState(false);
  const [assignForm, setAssignForm] = useState({ contact_id: '', category_id: '', due_date: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [catRes, assignRes, contactRes] = await Promise.all([
        api.get('/training/categories?include_materials=true'),
        api.get('/training/assignments?limit=50'),
        api.get('/contacts?status=active&limit=100')
      ]);
      setCategories(catRes.data.data);
      setAssignments(assignRes.data.data);
      setContacts(contactRes.data.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleAssign = async () => {
    try {
      await api.post('/training/assignments', assignForm);
      toast.success('Training assigned successfully');
      setAssignDialog(false);
      setAssignForm({ contact_id: '', category_id: '', due_date: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to assign'); }
  };

  const statusColors = { assigned: '#f59e0b', in_progress: '#3b82f6', completed: '#10b981' };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box><Typography variant="h4" sx={{ fontWeight: 700 }}>Training</Typography><Typography variant="body2" color="text.secondary">Manage training categories and assignments</Typography></Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {hasPermission('training_add') && (<><Button variant="outlined" onClick={() => navigate('/training/materials')}>Manage Materials</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAssignDialog(true)} sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>Assign Training</Button></>)}
        </Box>
      </Box>

      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Training Categories</Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {categories.map(cat => (
          <Grid item xs={12} sm={6} md={4} key={cat.id}>
            <Card sx={{ height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ bgcolor: cat.color || '#6366f1' }}><TrainingIcon /></Avatar>
                  <Box><Typography variant="h6" sx={{ fontWeight: 600 }}>{cat.name}</Typography><Typography variant="caption" color="text.secondary">{cat.materials?.length || 0} materials</Typography></Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>{cat.description}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Recent Assignments</Typography>
      <Card>
        <List>
          {assignments.length === 0 ? <Typography textAlign="center" py={4} color="text.secondary">No assignments yet</Typography> :
            assignments.slice(0, 10).map(a => (
              <ListItem key={a.id} divider>
                <ListItemAvatar><Avatar><PersonIcon /></Avatar></ListItemAvatar>
                <ListItemText primary={`${a.Contact?.first_name} ${a.Contact?.last_name}`} secondary={`${a.TrainingCategory?.name} • Due: ${a.due_date ? new Date(a.due_date).toLocaleDateString() : 'No deadline'}`} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 200 }}>
                  <Box sx={{ flex: 1 }}><LinearProgress variant="determinate" value={a.progress_percentage || 0} sx={{ height: 8, borderRadius: 4 }} /></Box>
                  <Typography variant="caption">{a.progress_percentage || 0}%</Typography>
                  <Chip label={a.status} size="small" sx={{ bgcolor: `${statusColors[a.status]}20`, color: statusColors[a.status], fontWeight: 500, fontSize: '0.7rem' }} />
                </Box>
              </ListItem>
            ))}
        </List>
      </Card>

      <Dialog open={assignDialog} onClose={() => setAssignDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Training</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}><FormControl fullWidth><InputLabel>Contact</InputLabel><Select value={assignForm.contact_id} label="Contact" onChange={(e) => setAssignForm({ ...assignForm, contact_id: e.target.value })}>{contacts.map(c => <MenuItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</MenuItem>)}</Select></FormControl></Grid>
            <Grid item xs={12}><FormControl fullWidth><InputLabel>Training Category</InputLabel><Select value={assignForm.category_id} label="Training Category" onChange={(e) => setAssignForm({ ...assignForm, category_id: e.target.value })}>{categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}</Select></FormControl></Grid>
            <Grid item xs={12}><TextField fullWidth label="Due Date" type="date" value={assignForm.due_date} onChange={(e) => setAssignForm({ ...assignForm, due_date: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions><Button onClick={() => setAssignDialog(false)}>Cancel</Button><Button variant="contained" onClick={handleAssign} disabled={!assignForm.contact_id || !assignForm.category_id}>Assign</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingList;
